// Labor tab in ticket detail — shows labor lines and inline add form.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { repairApi, RepairLabor, fmtVND } from "../repair-api";

interface Props {
  ticketId: string;
  labor: RepairLabor[];
  disabled?: boolean;
}

export function LaborSection({ ticketId, labor, disabled }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ description: "", amount: "" });

  const add = useMutation({
    mutationFn: () =>
      repairApi.addLabor(ticketId, {
        description: form.description,
        amount: form.amount || "0",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["repair-ticket", ticketId] });
      setForm({ description: "", amount: "" });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => repairApi.deleteLabor(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["repair-ticket", ticketId] }),
  });

  const total = labor.reduce((s, l) => s + parseFloat(l.amount || "0"), 0);

  return (
    <div className="space-y-4">
      {labor.length > 0 ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-muted-foreground text-xs">
              <th className="pb-2 text-left">Mô tả dịch vụ</th>
              <th className="pb-2 text-right">Thành tiền</th>
              <th className="pb-2" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {labor.map((l) => (
              <tr key={l.id}>
                <td className="py-2">{l.description}</td>
                <td className="py-2 text-right font-medium">{fmtVND(l.amount)}₫</td>
                <td className="py-2 text-right">
                  {!disabled && (
                    <button
                      onClick={() => remove.mutate(l.id)}
                      disabled={remove.isPending}
                      className="text-destructive hover:opacity-70 disabled:opacity-40"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t font-semibold">
              <td className="pt-2 text-right text-muted-foreground">Tổng nhân công:</td>
              <td className="pt-2 text-right">{total.toLocaleString("vi-VN")}₫</td>
              <td />
            </tr>
          </tfoot>
        </table>
      ) : (
        <p className="text-sm text-muted-foreground py-4 text-center">Chưa có nhân công / dịch vụ</p>
      )}

      {!disabled && (
        <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
          <p className="text-sm font-medium">Thêm nhân công / dịch vụ</p>
          <input
            type="text"
            placeholder="Mô tả (VD: Thay màn hình, cài lại hệ điều hành...)"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <input
            type="number"
            min={0}
            placeholder="Số tiền (VNĐ)"
            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
          />
          {add.isError && <p className="text-xs text-destructive">Lỗi khi thêm</p>}
          <button
            onClick={() => add.mutate()}
            disabled={!form.description || add.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-40"
          >
            {add.isPending ? "Đang thêm..." : "Thêm"}
          </button>
        </div>
      )}
    </div>
  );
}
